import React, { useState, useMemo, useEffect } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, Copy, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Column, Row } from './types';

type TableProps = {
  columns: Column[];
  rows: Row[];
};

const PAGE_SIZE = 15;

const TableDisplay: React.FC<TableProps> = ({ columns, rows }) => {
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [copiedCell, setCopiedCell] = useState<string | null>(null);

  // ✅ garante arrays (evita null/undefined sem precisar early return antes dos hooks)
  const safeColumns = columns ?? [];
  const safeRows = rows ?? [];

  const sortedRows = useMemo(() => {
    if (sortCol === null) return safeRows;

    return [...safeRows].sort((a, b) => {
      const valA = a.values[sortCol]?.str || '';
      const valB = b.values[sortCol]?.str || '';
      const numA = parseFloat(valA.replace(/[^0-9.-]/g, ''));
      const numB = parseFloat(valB.replace(/[^0-9.-]/g, ''));

      if (!isNaN(numA) && !isNaN(numB)) {
        return sortDir === 'asc' ? numA - numB : numB - numA;
      }

      return sortDir === 'asc'
        ? valA.localeCompare(valB, 'pt-BR')
        : valB.localeCompare(valA, 'pt-BR');
    });
  }, [safeRows, sortCol, sortDir]);

  // ✅ opcional, mas evita page “ficar fora” quando muda filtro/sort/rows
  useEffect(() => {
    setPage(0);
  }, [sortCol, sortDir, safeRows.length]);

  // ✅ agora pode retornar aqui sem quebrar hooks
  if (safeColumns.length === 0 || safeRows.length === 0) return null;

  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);
  const paginatedRows = sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSort = (colIdx: number) => {
    if (sortCol === colIdx) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(colIdx);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleCopy = (value: string, cellId: string) => {
    navigator.clipboard.writeText(value);
    setCopiedCell(cellId);
    setTimeout(() => setCopiedCell(null), 1500);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
              {safeColumns.map((column, idx) => (
                <th
                  key={`col-${column.position}`}
                  scope="col"
                  onClick={() => handleSort(idx)}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-200/50 transition-colors select-none group"
                >
                  <div className="flex items-center gap-1.5">
                    <span>{column.name}</span>
                    {sortCol === idx ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5 text-blue-600" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {paginatedRows.map((row, rowIndex) => (
              <tr key={`row-${page}-${rowIndex}`} className="hover:bg-blue-50/30 transition-colors duration-100">
                {row.values.map((value, valueIndex) => {
                  const cellId = `${page}-${rowIndex}-${valueIndex}`;
                  return (
                    <td
                      key={`cell-${cellId}`}
                      className="px-4 py-3 whitespace-nowrap text-sm text-gray-800 group/cell relative"
                    >
                      <div className="flex items-center gap-1">
                        <span>{value.str}</span>
                        <button
                          onClick={() => handleCopy(value.str, cellId)}
                          className="opacity-0 group-hover/cell:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-200"
                          title="Copiar"
                        >
                          {copiedCell === cellId ? (
                            <Check className="w-3 h-3 text-green-600" />
                          ) : (
                            <Copy className="w-3 h-3 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
          <span className="text-xs text-gray-500">
            {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, sortedRows.length)} de {sortedRows.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-600 px-2">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableDisplay;
